import * as _ from 'lodash';
import { Utils } from "../Utils/Utils";
import { StringUtils } from "../Utils/StringUtils";
import {WidgetApiBase} from './WidgetBase';

export class TreeViewApi extends WidgetApiBase {
    private _fancytree: Fancytree.Fancytree;

    constructor(id: string) {
        super(id);
    }

    protected get fancytree(): Fancytree.Fancytree {
        return this.getFancytree();
    }

    protected getInitOptions(): Object {
        return null;
    }

    protected getFancytree(doInit: boolean = false): Fancytree.Fancytree {
        if (!this._fancytree) {
            if (doInit) {
                this.jq.fancytree(this.getInitOptions());
            }
            this._fancytree = this.jq.fancytree("getTree");
        }

        return this._fancytree;
    }

    public expandAll(): void {
        this.fancytree.visit(node => node.setExpanded(true));
    }

    public collapseAll(): void {
        this.fancytree.visit(node => node.setExpanded(false));
    }

    public selectAll(select: boolean): void {
        this.fancytree.visit(node => {
            node.setSelected(select);
        });
    }

    public visit(visitFn: (node: Fancytree.FancytreeNode) => void): void {
        this.fancytree.visit(node => {
            visitFn(node);
        });
    }

    public getSelectedNodeKeys(filter?: (node: Fancytree.FancytreeNode) => boolean): string[] {
        var nodes = this.fancytree.getSelectedNodes();
        if (filter) {
            nodes = _.filter(nodes, filter);
        }
        return _.map(nodes, n => n.key);
    }

    public getNodeByKey(key: string, searchRoot?: Fancytree.FancytreeNode): Fancytree.FancytreeNode {
        return this.fancytree.getNodeByKey(key, searchRoot);
    }
}

export class TreeView extends TreeViewApi {
    private _options: TreeViewOptions;
    private _selectionInput: JQuery;
    private _initialized: boolean;

    constructor(id: string, options?: TreeViewOptions) {
        super(id);
        this._initialized = false;

        var hiddenIdSelection = `#${id}-selection-hdn`;
        this._selectionInput = $(hiddenIdSelection);

        this._options = options;
    }

    // events
    public onInit: TreeViewEvent;
    public onRenderNode: TreeViewEvent;
    public onAcceptSelection: TreeViewAcceptSelectionEvent;
    public onSelectionChanged: TreeViewEvent;

    // methods
    public get options(): TreeViewOptions {
        return this._options;
    }

    public set options(value: TreeViewOptions) {
        this.assertOptions(value);
        this._options = value;
    }

    private assertOptions(opt?: TreeViewOptions): void {
        var o = opt || this._options;

        if (Utils.isNullOrUndefined(o)) {
            throw new Error('Options for TreeView could not be null or undefined!');
        }
    }

    public initialize(): void {
        if (this._initialized) {
            throw new Error(`Tree view '${this._id}' was already initialized!`);
        }

        this.assertOptions();
        this.handleInit = this.handleInit.bind(this);
        this.handleBeforeSelect = this.handleBeforeSelect.bind(this);
        this.handleRenderNode = this.handleRenderNode.bind(this);
        this.handleSelect = this.handleSelect.bind(this);

        this.getFancytree(true);
        this.updateSelection();
        this._initialized = true;
    }

    public static get(id: string): TreeView {
        var tv = window[`_Neo_TreeView_${id}`];
        return tv as TreeView;
    }

    private updateSelection(): void {
        var self = this;
        var selectionJson: string[] = [];
        this.fancytree.visit(node => {
            if (node.isSelected()) {
                if (self.canAcceptSelection(node)) {
                    selectionJson.push(node.key);
                }
            }
        });
        this._selectionInput.val(JSON.stringify(selectionJson));
    }

    protected getInitOptions(): Object {
        var self = this;
        var o = this._options;

        var fancyTreeOptions: Fancytree.FancytreeOptions = {
            selectMode: o.selectMode,
            quicksearch: o.quicksearch,
            focusOnSelect: o.focusOnSelect,
            checkbox: o.checkbox,
            activeVisible: o.activeVisible,
            autoActivate: o.autoActivate,
            autoCollapse: o.autoCollapse,
            autoScroll: o.autoScroll,
            clickFolderMode: o.clickFolderMode,
            minExpandLevel: o.minExpandLevel,
            source: o.source,
            init: this.handleInit,
            beforeSelect: this.handleBeforeSelect,
            renderNode: this.handleRenderNode,
            select: this.handleSelect
        };

        (fancyTreeOptions as any).escapeTitles = o.escapeTitles;
        (fancyTreeOptions as any).icon = o.icon;

        return fancyTreeOptions;
    }

    private canAcceptSelection(node: Fancytree.FancytreeNode): boolean {
        var res: boolean = undefined;

        if (this.onAcceptSelection) {
            res = this.onAcceptSelection({
                fancyNode: node
            });
        }

        return Utils.valueOrDefault(res, true);
    }

    private fireOnInit(event: JQueryEventObject, data: Fancytree.EventData) {
        if (!Utils.isNullOrUndefined(this.onInit)) {
            this.onInit(event, {
                treeView: this,
                node: {
                    fancyNode: data.node
                }
            });
        }
    }

    private fireOnRenderNode(event: JQueryEventObject, data: Fancytree.EventData) {
        if (!Utils.isNullOrUndefined(this.onRenderNode)) {
            this.onRenderNode(event, {
                treeView: this,
                node: {
                    fancyNode: data.node
                }
            });
        }
    }

    private fireOnSelectionChanged(event: JQueryEventObject, data: Fancytree.EventData) {
        if (!Utils.isNullOrUndefined(this.onSelectionChanged)) {
            this.onSelectionChanged(event, {
                treeView: this,
                node: {
                    fancyNode: data.node
                }
            });
        }
    }

    protected handleSelect(event: JQueryEventObject, data: Fancytree.EventData): void {
        this.updateSelection();

        this.fireOnSelectionChanged(event, data);
    }

    protected handleBeforeSelect(event: JQueryEventObject, data: Fancytree.EventData): boolean {
        var node = data.node;
        var canSelect = (node as any).unselectable === false;
        return canSelect;
    }

    protected handleRenderNode(event: JQueryEventObject, data: Fancytree.EventData): void {
        this.fireOnRenderNode(event, data);
    }

    protected handleInit(event: JQueryEventObject, data: Fancytree.EventData): void {
        var tree = data.tree;
        var o = this._options;

        var h = o.height;
        if (h && h > 0) {
            var ul = tree.$container;
            ul.css('height', `${h}px`);
            ul.css('overflowY', 'auto');

            if (!StringUtils.isNullOrEmpty(o.ulcss)) {
                ul.addClass(o.ulcss);
            }

            if (!o.border) {
                ul.addClass('no-border');
            }
        }

        tree.visit(node => {
            node.setSelected(node.data.preselected);
        });

        this.fireOnInit(event, data);
    }

    public static updateNodeDisabled(node: Fancytree.FancytreeNode, disabled: boolean): void {
        var jq = $((node as any).li);
        if (disabled) {
            jq.addClass('fancytree-graytext');
        } else {
            jq.removeClass('fancytree-graytext');
        }
    }

    public static updateNodeStrikeThrough(node: Fancytree.FancytreeNode, strikeThrough: boolean): void {
        var jq = $((node as any).li);
        if (strikeThrough) {
            jq.addClass('fancytree-strikethrough');
        } else {
            jq.removeClass('fancytree-strikethrough');
        }
    }
}

export type TreeViewGetIconFunction = (event: { type: string }, data: { node: Fancytree.FancytreeNode }) => string;

export interface TreeViewOptions {
    // fancy tree options
    selectMode: Fancytree.FancytreeSelectMode;
    quicksearch: boolean;
    focusOnSelect: boolean;
    escapeTitles: boolean;
    checkbox: boolean;
    activeVisible: boolean;
    autoActivate: boolean;
    autoCollapse: boolean;
    autoScroll: boolean;
    clickFolderMode: Fancytree.FancytreeClickFolderMode;
    minExpandLevel: number;
    icon?: boolean | TreeViewGetIconFunction;
    source: TreeViewNodeData[];

    // custom treview options
    height?: number;
    ulcss?: string;
    border: boolean;
}

export type TreeViewEvent = (event: JQueryEventObject, data: TreeViewEventData) => void;

export type TreeViewAcceptSelectionEvent = (node: TreeViewNode) => boolean;

/**
 * @TreeViewNodeData - used to initialize tree, at runtime use 'TreeViewNode'
 */
export interface TreeViewNodeData extends Fancytree.NodeData {
    children?: TreeViewNodeData[];
}

export interface TreeViewNode {
    fancyNode: Fancytree.FancytreeNode;
}

export type TreeViewEventData = {
    treeView: TreeViewApi;
    node: TreeViewNode;
};



/** WEBPACK FOOTER **
 ** ./Typescript/Widgets/TreeView.ts
 **/